import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles } from 'lucide-react';

interface AIInsightsLoadingStateProps {
  message?: string;
  isDualAssessment?: boolean;
}

const AIInsightsLoadingState: React.FC<AIInsightsLoadingStateProps> = ({ 
  message = "Analyzing your behavioral profile...",
  isDualAssessment = false
}) => {
  const loadingMessages = [
    "Analyzing your behavioral profile...",
    "Identifying key personality patterns...",
    "Crafting personalized insights...",
    "Understanding your unique strengths..."
  ];

  const [currentMessage, setCurrentMessage] = React.useState(message);

  React.useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % loadingMessages.length;
      setCurrentMessage(loadingMessages[index]);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const renderSkeletonBars = () => (
    <div className="space-y-4 w-full">
      {[1, 2, 3, 4].map((index) => (
        <div 
          key={index}
          className="border-l-4 border-muted pl-4 sm:pl-6 py-2 w-full"
          style={{ 
            animationDelay: `${index * 150}ms`,
            opacity: 0.4 + (index * 0.15)
          }}
        >
          <Skeleton className="h-4 bg-muted" style={{ width: `${85 - (index * 10)}%` }} />
        </div>
      ))}
    </div>
  );

  const LoadingHeader = () => (
    <div className="flex items-center gap-3 mb-6">
      <div className="relative">
        <div className="absolute inset-0 bg-primary/20 rounded-full blur-md animate-pulse" />
        <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 border border-primary/20">
          <Sparkles className="w-5 h-5 text-primary animate-pulse" />
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">AI is generating insights</p>
        <p className="text-xs text-muted-foreground transition-all duration-500">
          {currentMessage}
        </p>
      </div>
    </div>
  );

  if (isDualAssessment) {
    return (
      <div className="space-y-10 w-full py-4">
        <LoadingHeader />
        
        {/* Self section skeleton */}
        <div className="space-y-4 w-full">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded bg-muted" />
            <Skeleton className="h-6 w-32 bg-muted" />
          </div>
          {renderSkeletonBars()}
        </div>

        {/* Work section skeleton */}
        <div className="space-y-4 w-full pt-6 border-t border-border/50">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded bg-muted" />
            <Skeleton className="h-6 w-36 bg-muted" />
          </div>
          {renderSkeletonBars()}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-4">
      <LoadingHeader />
      {renderSkeletonBars()}
    </div>
  );
};

export default AIInsightsLoadingState;
